import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";

export default function CareerFormSection() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    resumeUrl: "",
    experience: "",
    previousCompany: "",
    motivation: "",
    agreement: false,
  });

  const [errors, setErrors] = useState({});
  const [formStatus, setFormStatus] = useState("");

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required.";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "A valid email is required.";
    if (!formData.phone.trim() || !/^\d{10}$/.test(formData.phone))
      newErrors.phone = "A valid 10-digit phone number is required.";
    if (!formData.resumeUrl.trim() || !/^https?:\/\/.+$/.test(formData.resumeUrl))
      newErrors.resumeUrl = "A valid URL is required.";
    if (!formData.experience.trim() || isNaN(Number(formData.experience)))
      newErrors.experience = "Experience must be a number.";
    if (!formData.motivation.trim())
      newErrors.motivation = "This field is required.";
    if (!formData.agreement)
      newErrors.agreement = "You must agree to create a happy environment.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Simulating form submission
    try {
      const response = await fetch("/New-Zwilling-Website-PHP/send_career_form.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          resumeUrl: formData.resumeUrl,
          experience: formData.experience,
          previousCompany: formData.previousCompany,
          motivation: formData.motivation,
        })
      });

      const result = await response.json();

      if (result.status === "success") {
        toast.success(result.message);
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          resumeUrl: "",
          experience: "",
          previousCompany: "",
          motivation: "",
          agreement: false,
        })
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("There was an error submitting the form. Please try again later.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <div className="mt-[252px]">
      <div className="flex justify-center bg-blue-50 py-32 md:px-10 md:py-5">
        <div className="container-xs mb-2.5 flex justify-center px-2.5 sm:px-5">
          <div className="flex w-full flex-col gap-11">
            {/* Heading Section */}
            <div className="mr-3 flex flex-col items-center px-14 md:mr-0 md:px-5">
              <h2 className="text-[46px] font-bold uppercase tracking-[4.60px] text-gray-800 md:text-[42px] sm:text-[36px]">
                Join Us
              </h2>
              <p className="text-[26px] font-normal text-gray-600 md:text-[24px] sm:text-[22px]">
                Fill out the form
              </p>
            </div>

            {/* Form Section */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-[42px]">
              {/* Full Name and Email */}
              <div className="flex md:flex-col gap-4">
                <div className="flex flex-1 flex-col">
                  <label htmlFor="fullName" className="text-[20px] text-gray-800">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    id="fullName"
                    placeholder="Full name"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full rounded border-gray-300 p-2 bg-white"
                  />
                  {errors.fullName && <span className="text-red-500">{errors.fullName}</span>}
                </div>
                <div className="flex flex-1 flex-col">
                  <label htmlFor="email" className="text-[20px] text-gray-800">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email ID"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded border-gray-300 p-2 bg-white"
                  />
                  {errors.email && <span className="text-red-500">{errors.email}</span>}
                </div>
              </div>

              {/* Phone and Resume URL */}
              <div className="flex md:flex-col gap-4">
                <div className="flex flex-1 flex-col">
                  <label htmlFor="phone" className="text-[20px] text-gray-800">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    placeholder="Phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded border-gray-300 p-2 bg-white"
                  />
                  {errors.phone && <span className="text-red-500">{errors.phone}</span>}
                </div>
                <div className="flex flex-1 flex-col">
                  <label htmlFor="resumeUrl" className="text-[20px] text-gray-800">
                    Resume URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    name="resumeUrl"
                    id="resumeUrl"
                    placeholder="URL"
                    value={formData.resumeUrl}
                    onChange={handleChange}
                    className="w-full rounded border-gray-300 p-2 bg-white"
                  />
                  {errors.resumeUrl && <span className="text-red-500">{errors.resumeUrl}</span>}
                </div>
              </div>

              {/* Experience and Previous Company */}
              <div className="flex md:flex-col gap-4">
                <div className="flex flex-1 flex-col">
                  <label htmlFor="experience" className="text-[20px] text-gray-800">
                    Experience (in years) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="experience"
                    id="experience"
                    placeholder="Experience (in years)"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full rounded border-gray-300 p-2 bg-white"
                  />
                  {errors.experience && <span className="text-red-500">{errors.experience}</span>}
                </div>
                <div className="flex flex-1 flex-col">
                  <label htmlFor="previousCompany" className="text-[20px] text-gray-800">
                    Previous Company (If any)
                  </label>
                  <input
                    type="text"
                    name="previousCompany"
                    id="previousCompany"
                    placeholder="Company name"
                    value={formData.previousCompany}
                    onChange={handleChange}
                    className="w-full rounded border-gray-300 p-2 bg-white"
                  />
                </div>
              </div>

              {/* Motivation */}
              <div className="flex flex-col">
                <label htmlFor="motivation" className="text-[20px] text-gray-800">
                  Why do you want to join our company? <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="motivation"
                  id="motivation"
                  placeholder="Your answer"
                  value={formData.motivation}
                  onChange={handleChange}
                  className="w-full rounded border-gray-300 p-2 bg-white"
                />
                {errors.motivation && <span className="text-red-500">{errors.motivation}</span>}
              </div>

              {/* Agreement and Submit */}
              <div className="flex items-center gap-4">
                <input
                  type="checkbox"
                  name="agreement"
                  id="agreement"
                  checked={formData.agreement}
                  onChange={handleChange}
                />
                <label htmlFor="agreement" className="text-[18px] text-gray-800">
                  I agree to create a happy environment in the team
                </label>
              </div>
              {errors.agreement && <span className="text-red-500">{errors.agreement}</span>}

              <button
                type="submit"
                className="self-center rounded bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
              >
                Apply
              </button>
              <ToastContainer position="top-right" />
            </form>

          </div>
        </div>
      </div>
    </div>
  );
}
